import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { AppRequiredMessage } from "../../../app/AppRequiredMessage";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";
import { useAppDispatch } from "../../../app/hooks";
import { If } from "../../../app/If";
import { StatusLevelAlert } from "../../../app/components/StatusLevelAlert";
import { CheckSerialNoResponse, Device, checkSerialNo, updateDeviceSerialNo } from "../deviceSlice";
import { Help } from "../../dialogs/HelpDialog";

export interface EditDeviceSerialNoFieldProps {
    device: Device;
    onSave: (device: Device) => void;
    onCancel: () => void;
}

export const EditDeviceSerialNoField = (props: EditDeviceSerialNoFieldProps) => {
    const { device, onCancel, onSave } = props;
    const dispatch = useAppDispatch();

    const [unknownNo, setUnknownNo] = useState(device.unknownNo ?? false);
    const [serialNo, setSerialNo] = useState(device.serialNo ?? "");
    const [validationErrors, setValidationErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const [serialValidation, setSerialValidation] = useState<CheckSerialNoResponse | null>(null);
    const [serialValidationBusy, setSerialValidationBusy] = useState(false);
    const serialValidationRequestRef = useRef(0);
    const serialValidationHideBusyTimeoutRef = useRef<number | null>(null);

    const serialNoError = validationErrors && !unknownNo && serialNo.trim() === "";

    useEffect(() => {
        if(unknownNo) {
            setSerialValidation(null);
            setSerialValidationBusy(false);
            return;
        }
        
        if (serialValidationHideBusyTimeoutRef.current !== null) {
            window.clearTimeout(serialValidationHideBusyTimeoutRef.current);
            serialValidationHideBusyTimeoutRef.current = null;
        }

        if (unknownNo) {
            setSerialValidation(null);
            setSerialValidationBusy(false);
            return;
        }

        const normalizedSerialNo = serialNo.trim();
        const structureId = Number(device.structureId);
        const supplierId = Number(device.supplierId);

        if (!normalizedSerialNo || !Number.isInteger(structureId) || structureId <= 0 || !Number.isInteger(supplierId) || supplierId <= 0) {
            setSerialValidation(null);
            setSerialValidationBusy(false);
            return;
        }

        const requestId = serialValidationRequestRef.current + 1;
        serialValidationRequestRef.current = requestId;

        const timeoutId = window.setTimeout(async () => {
            const busyShownAt = Date.now();
            setSerialValidationBusy(true);

            const result = await dispatch(checkSerialNo({
                deviceId: device.id ? Number(device.id) : undefined,
                serialNo: normalizedSerialNo,
                structureId,
                supplierId,
            }));

            if (serialValidationRequestRef.current !== requestId) {
                return;
            }

            if (checkSerialNo.fulfilled.match(result)) {
                setSerialValidation(result.payload as CheckSerialNoResponse);
            } else {
                setSerialValidation({ status: "SERVER_ERROR", level: 4 });
            }

            const elapsed = Date.now() - busyShownAt;
            const remaining = 350 - elapsed;

            if (remaining > 0) {
                serialValidationHideBusyTimeoutRef.current = window.setTimeout(() => {
                    if (serialValidationRequestRef.current !== requestId) {
                        return;
                    }
                    setSerialValidationBusy(false);
                }, remaining);
            } else {
                setSerialValidationBusy(false);
            }
        }, 500);

        return () => {
            window.clearTimeout(timeoutId);
            if (serialValidationHideBusyTimeoutRef.current !== null) {
                window.clearTimeout(serialValidationHideBusyTimeoutRef.current);
                serialValidationHideBusyTimeoutRef.current = null;
            }
        };
    }, [device.id, device.structureId, device.supplierId, dispatch, serialNo, unknownNo]);

    const handleSaveClick = async () => {
        setValidationErrors(true);
        setServerError("");

        if (!unknownNo && serialNo.trim() === "") {
            return;
        }

        setSaving(true);
        try {
            const result = await dispatch(updateDeviceSerialNo({
                deviceId: Number(device.id),
                unknownNo,
                serialNo: unknownNo ? "" : serialNo.trim(),
            }));

            if (updateDeviceSerialNo.fulfilled.match(result)) {
                onSave(result.payload as Device);
            } else {
                const error = result.payload as any;
                const errorMessage = error?.problem?.detail || error?.problem?.title || "Validation error occurred";
                setServerError(errorMessage);
            }
        } catch (e: any) {
            const msg = e?.payload?.problem?.title ?? e?.message ?? "Error occurred during saving.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}
            >
                <Localized id="edit">Edycja</Localized>
            </DialogTitle>

            <DialogContent sx={{ pt: "10px !important" }}>
                <Box
                    sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "grey.50",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                        gap: 2
                    }}
                >
                    <FormControlLabel
                        sx={{ m: 0, flex: "0 0 auto" }}
                        control={<Checkbox checked={unknownNo} onChange={(e) => setUnknownNo(e.target.checked)} />}
                        label={<Localized id="device-unknown-no"><span>Nieznany numer fabryczny</span></Localized>}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label={<Localized id="devices-serial-number">Numer fabryczny</Localized>}
                        value={serialNo}
                        error={serialNoError}
                        helperText={serialNoError ? <AppRequiredMessage /> : undefined}
                        onChange={(e) => setSerialNo(e.target.value)}
                        disabled={unknownNo}
                    />
                    <Help id="32b01a5b-7e8b-43bc-a354-5f67eee66aa8" />
                </Box>

                <If condition={unknownNo === false && (serialValidationBusy || !!serialValidation)}>
                    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        {serialValidationBusy ? (
                            <Alert severity="info" sx={{ m: 0 }}>Sprawdzanie numeru seryjnego...</Alert>
                        ) : serialValidation ? (
                            <StatusLevelAlert type="DEVICE" status={serialValidation.status} level={serialValidation.level} sx={{ m: 0 }} />
                        ) : null}
                    </Box>
                </If>

                {serverError && <Alert sx={{ mt: 2 }} severity="error">{serverError}</Alert>}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};